<template>
  <!-- class="add-employee-dialog" -->
  <global-dialog-card
    v-model="model"
    :max-width="700"
    :title="formTitle"
    :subtitle="employee?.id ? `ID: ${employee.id}` : ''"
  >
    <global-confirm-box ref="confirmBox"></global-confirm-box>

    <employee-avatar-section :employee="employee" :conf="conf" @upload="upload" />

    <v-form v-model="valid">
      <employee-form-section
        :employee="employee"
        :parent-companies="parentCompanies"
        :companies="companies"
        :error-message-email="errorMessageEmail"
        :searchs.sync="searchs"
        :selected-company="selectedCompany"
        :delivery-company="deliveryCompany"
        :is-delivery="isDeliveryRoute"
        @open-change-company="openChangeCompany"
      />
    </v-form>
    <template #actions>
      <v-btn text @click="model = false">
        {{ $t("cancel") }}
      </v-btn>
      <primary-button :dark="valid" @click="save" :loading="loading" :disabled="!valid">
        {{ $t("save") }}
      </primary-button>
    </template>
  </global-dialog-card>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import deliveryUsersContext from "@/mixins/deliveryUsersContext";
import employeeService from "@/services/employee";
import conf from "@/config";

// Import new subcomponents
import EmployeeAvatarSection from "./EmployeeAvatarSection.vue";
import EmployeeFormSection from "./EmployeeFormSection.vue";

export default {
  mixins: [hasModel, hasSnackbar, deliveryUsersContext],
  name: "CompanyEmployeeDialog",
  components: {
    EmployeeAvatarSection,
    EmployeeFormSection,
  },
  props: {
    employee: {
      type: Object,
      default: () => ({
        id: 0,
      }),
    },
    selectedCompany: {
      type: Object,
      required: false,
    },
    companies: {
      type: Array,
      default: () => [],
    },
    parentCompanies: {
      type: Array,
      default: () => [],
    },
    // The delivery firm itself (its own record - name/settings/etc, not the
    // employee list) - needed to know its currency for the courier payment
    // amount suffix. Distinct from `selectedCompany`/`companies`, which come
    // from the unrelated /companies endpoint and don't carry delivery firm
    // data in this route.
    deliveryCompany: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      conf,
      errorMessageEmail: "",
      searchs: [],
      loading: false,
      valid: false,
    };
  },
  computed: {
    formTitle() {
      return this.employee.id ? this.$t("edit_user") : this.$t("new_user");
    },
    companyId() {
      return this.$route.params.id || this.$store.state.company?.id;
    },
    isDeliveryRoute() {
      return this.isDeliveryUsersContext;
    },
    isRestaurantRoute() {
      return this.$route.name === "restoran-id-korisnici";
    },
  },
  methods: {
    async save() {
      this.errorMessageEmail = "";

      if (!this.employee.hasOwnProperty("type")) {
        this.errorMessageEmail = this.$t("type_field_required");
        return;
      }

      this.loading = true;

      try {
        if (this.employee.id) {
          // Update existing user
          await this.updateEmployee();
        } else {
          // Create new user
          await this.createEmployee();
        }
        // Zatvori dijalog SAMO na uspjeh - na grešku (npr. "email već
        // postoji") korisnik treba da vidi poruku i ispravi samo taj field,
        // ne da ponovo unosi sve od početka.
        this.model = false;
      } catch (error) {
        this.handleSaveError(error);
      } finally {
        this.loading = false;
      }
    },

    async updateEmployee() {
      try {
        if (this.isDeliveryRoute) {
          // Dedicated delivery endpoint — never touch PATCH /user/:id, which
          // rewires the company relation that does not apply to delivery.
          // Only name / lastname / phone / type are documented as accepted
          // (09_09_2026 backend answer, point 3); `email` is never sent (removed
          // from this endpoint, 11_09_2026 backend answer pt 4). `password` is
          // only added when a platform admin actually typed one (form hides the
          // field otherwise) — unconfirmed with backend that this endpoint
          // accepts it, needs verifying against the API.
          const body = {
            name: this.employee.name,
            lastname: this.employee.lastname,
            phone: this.employee.phone,
            type: this.employee.type,
          };
          if (this.employee.password) {
            body.password = this.employee.password;
          }
          Object.assign(body, this.buildDeliveryExtraFieldsBody());
          await employeeService.updateUserDelivery(this.companyId, this.employee.id, body);
        } else {
          const userData = {
            ...this.employee,
            company_id: this.employee.company?.id || this.employee.company_id,
          };
          await employeeService.updateUser(this.employee.id, userData);
        }
        this.$emit("employeeUpdated");
        this.showSuccessSnackbar(this.$t("user_updated_successfully"));
      } catch (error) {
        console.warn("Error updating employee:", error);
        if (error.response?.data?.status === "failed") {
          this.errorMessageEmail = error.response.data.message;
        }
        throw error;
      }
    },

    async createEmployee() {
      const userData = {
        ...this.employee,
        can_report: this.isDeliveryRoute ? 0 : this.employee.can_report,
      };
      // NE dodavati buildDeliveryExtraFieldsBody() (flat polja) ovdje -
      // testirano uživo 15.09, slanje flat polja UZ postojeći nested
      // pivot/detail obara POST sa 500 (radilo je dok su išli SAMO nested,
      // samo su se tiho ignorisali - vidi N1 u
      // 15_09_2026_Frontend_pitanja_za_backend_korisnici_dostava.textile).
      // Ostaju samo nested pivot/detail iz `...this.employee` spread-a ispod
      // - i dalje se tiho ignorišu dok backend ne popravi kreiranje, ali bar
      // ne ruše endpoint.

      try {
        let response;
        if (this.isRestaurantRoute) {
          response = await employeeService.createRestaurantUser(
            this.companyId,
            userData,
            this.isDeliveryRoute
          );
        } else {
          response = await employeeService.createCompanyUser(
            this.companyId,
            userData,
            this.isDeliveryRoute
          );
        }

        this.$emit("employeeAdded", response.data);
        this.showSuccessSnackbar(this.$t("user_created_successfully"));
      } catch (error) {
        if (error.response?.data?.status === "failed") {
          this.errorMessageEmail = error.response.data.message;
        }
        throw error;
      }
    },

    // Lični podaci / Naplata i ugovor / Preporuke polja - isti flat body kao
    // za PATCH .../users-delivery/:id (14.09.2026 backend answer). Dijeljen
    // između updateEmployee (PATCH - stvarno čuva) i createEmployee (POST -
    // trenutno tiho ignoriše, vidi N3 u
    // 14_09_2026_Frontend_pitanja_za_backend_korisnici_dostava.textile).
    // Namjerno NEMA create-pa-odmah-PATCH zaobilaznice na frontendu - to
    // treba backend da riješi na samom POST-u.
    buildDeliveryExtraFieldsBody() {
      const pivot = this.employee.pivot || {};
      const detail = this.employee.detail || {};
      return {
        bank_account: pivot.bank_account,
        paying_type: pivot.paying_type,
        paying: pivot.paying,
        note: pivot.note,
        contract_signed_at: pivot.contract_signed_at,
        contract_active_from: pivot.contract_active_from,
        date_of_birth: detail.date_of_birth,
        iban: detail.iban,
        emergency_contact_name: detail.emergency_contact_name,
        emergency_contact_phone: detail.emergency_contact_phone,
        referral_url: detail.referral_url,
        referral_short_url: detail.referral_short_url,
        referred_by: detail.referred_by,
      };
    },

    handleSaveError(error) {
      const message = error.response?.data?.message || "user_create_error_prefix";
      this.showErrorSnackbar(this.$t(message));
    },

    async upload(file) {
      if (!file) return;

      const formData = new FormData();
      formData.append("image", file);
      formData.append("image_path", "users/photos/");

      try {
        const { data } = await employeeService.uploadUserPhoto(formData);
        this.employee.image_path = "users/photos/small/" + data;
        this.showSuccessSnackbar(
          this.$t("photo_uploaded_successfully") || "Fotografija uspešno otpremljena"
        );
      } catch (error) {
        this.showErrorSnackbar(
          this.$t("photo_upload_error") || "Greška pri otpremanju fotografije"
        );
      }
    },

    openChangeCompany() {
      this.$refs.confirmBox
        .open(this.$t("change_user_company"), this.$t("change_company_confirmation"), {
          color: "warning",
        })
        .then(() => {
          this.changeCompany();
        })
        .catch((err) => console.warn(err));
    },

    async changeCompany() {
      try {
        await employeeService.changeUserCompany(this.employee);
        this.showSuccessSnackbar(
          this.$t("company_changed_successfully") || "Kompanija uspešno promenjena"
        );
        this.$emit("companyUpdated");
        this.dialog = false;
      } catch (error) {
        this.showErrorSnackbar(
          this.$t("company_change_error") || "Greška pri promeni kompanije"
        );
      }
    },
  },
};
</script>
